import { useSyncExternalStore } from "react";
import api from "./api";
import { SESSION_ENDED } from "./sessionFailure";
type Details = {
  questionTimeSpent?: Record<string, number>;
  totalTimeSpent?: number;
  questionsAnswered?: number;
  questionsMarked?: number;
};
type Pending = {
  eventId: string;
  testId: string;
  token: string;
  details: Details;
  busy: boolean;
};
let queue: Pending[] = [];
let pending: Pending | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());
export const clearAuditDelivery = () => {
  queue = [];
  pending = null;
  notify();
};
window.addEventListener(SESSION_ENDED, clearAuditDelivery);
window.addEventListener("storage", (event) => {
  if (event.key === "token") clearAuditDelivery();
});
async function send(event: Pending) {
  if (localStorage.getItem("token") !== event.token) {
    clearAuditDelivery();
    return;
  }
  try {
    const { data } = await api.post("/student/audit/test-analytics", {
      eventId: event.eventId,
      testId: event.testId,
      ...event.details,
    });
    if (data?.eventId !== event.eventId || data?.status !== "PERSISTED")
      throw new Error("Activity receipt missing");
    queue = queue.filter((item) => item.eventId !== event.eventId);
    pending = queue[0] ?? null;
    notify();
  } catch {
    if (localStorage.getItem("token") === event.token) {
      queue = queue.map((item) =>
        item.eventId === event.eventId ? { ...event, busy: false } : item,
      );
      pending = queue[0] ?? null;
      notify();
    }
  }
}
export function deliverAudit(testId: string, details: Details) {
  const token = localStorage.getItem("token");
  if (!token) return;
  const event = {
    eventId: crypto.randomUUID(),
    testId,
    token,
    details,
    busy: true,
  };
  queue = [...queue, event];
  pending = queue[0];
  notify();
  void send(event);
}
export function AuditDeliveryNotice() {
  const event = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => pending,
  );
  if (!event || event.busy) return null;
  return (
    <aside
      role="status"
      className="m-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-slate-900"
    >
      <p>Your test result is saved. Activity details could not be saved.</p>
      <button
        type="button"
        disabled={event.busy}
        className="mt-2 min-h-11 underline"
        onClick={() => {
          queue = queue.map((item) =>
            item.eventId === event.eventId ? { ...event, busy: true } : item,
          );
          pending = queue[0];
          notify();
          void send({ ...event, busy: true });
        }}
      >
        {event.busy ? "Retrying…" : "Retry activity details"}
      </button>
    </aside>
  );
}
